import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SettingsNav, { type SettingsTab } from '../components/settings/SettingsNav'
import AccountSettings from '../components/settings/AccountSettings'
import PreferencesSettings from '../components/settings/PreferencesSettings'
import CrmSettings from '../components/settings/CrmSettings'
import TeamSettings from '../components/settings/TeamSettings'
import { buildSettingsRoute } from '../lib/routes'

interface Props {
  onBack: () => void
  /** Section to show. Defaults to 'account' when not provided. URL-driven
   * since ADR 0026 so links like /settings/crm deep-link straight to the
   * CRM tab. */
  section?: SettingsTab | null
}

// TEMP: 'billing' removed for the Apr-23 demo — see SettingsNav.tsx. Without
// this, a stale /settings/billing URL would still render the Coming Soon
// placeholder even though the sidebar entry is hidden. Put 'billing' back
// when Phase 5 lands.
const VALID_TABS: ReadonlyArray<SettingsTab> = ['account', 'preferences', 'crm', 'team']

export default function Settings({ onBack, section }: Props) {
  const navigate = useNavigate()
  const tab: SettingsTab = section && VALID_TABS.includes(section) ? section : 'account'
  const setTab = (next: SettingsTab) => navigate(buildSettingsRoute(next))

  return (
    <div className="flex flex-1 overflow-hidden bg-zinc-50">
      {/* Left nav */}
      <div className="flex w-[240px] flex-col border-r border-zinc-200 bg-white">
        <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-[18px]">
          <button
            onClick={onBack}
            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
            title="Back to inbox"
            aria-label="Back to inbox"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-zinc-900">Settings</h2>
        </div>
        <SettingsNav active={tab} onSelect={setTab} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div key={tab} className="animate-fade-in mx-auto max-w-3xl px-8 py-8">
          {tab === 'account' && <AccountSettings />}
          {tab === 'preferences' && <PreferencesSettings />}
          {tab === 'crm' && <CrmSettings />}
          {tab === 'team' && <TeamSettings />}
          {tab === 'billing' && <BillingComingSoon />}
        </div>
      </div>
    </div>
  )
}

function ComingSoon({ label, description }: { label: string; description: string }) {
  return (
    <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center">
      <h3 className="text-sm font-semibold text-zinc-900">{label}</h3>
      <p className="mt-1 text-sm text-zinc-500">{description}</p>
    </div>
  )
}

/**
 * BillingComingSoon — wraps the generic ComingSoon with an interest-capture
 * effect so product can gauge demand for the billing tab before Phase 5 ships.
 *
 * Tracking strategy: the SETTINGS_BILLING_VIEW sessionStorage key debounces
 * repeat hits within a single session (a user flipping between tabs shouldn't
 * count ten times). Once PostHog or equivalent is wired, swap the
 * console.info line for `analytics.track('billing_tab_viewed')`.
 */
function BillingComingSoon() {
  useEffect(() => {
    try {
      const key = 'fwdslash:settings_billing_viewed'
      if (sessionStorage.getItem(key)) return
      // Sentinel value — we only care about existence (the getItem guard
      // above). Date.now() invited "this will be read later for freshness"
      // misreads; `'1'` makes the single-bit intent explicit.
      sessionStorage.setItem(key, '1')
      // One-liner breadcrumb. Replace with analytics.track() when that exists.
      console.info('[settings] billing_tab_viewed')
    } catch {
      // sessionStorage can throw in private-mode or sandboxed contexts — a
      // missed view is fine, don't let it tear down the settings tab.
    }
  }, [])
  return <ComingSoon label="Billing" description="Manage your subscription. Coming in Phase 5." />
}
